// covered-by: packages/schemas/src/christo/__tests__/chatAtoms.test.ts
import { z } from "zod";

/**
 * Chat atoms an agent asked the client to draw on an assistant message.
 *
 * warhol validates every payload against `services/warhol/contracts/components.py`
 * before it reaches christo, and christo renames snake_case to camelCase at the
 * border (`warholComponentsForChat`). These schemas are that camelCase shape:
 * what `askPunttchats.messages[].components` stores and what Carrie draws.
 *
 * Payloads carry data only. Every Carrie prop that is a callback is absent on
 * purpose: the agent says what to draw, never what happens when it is pressed.
 * The person's next message, in words, is the answer
 * (warhol spec kevin.johnson/0001), so no atom carries a status either.
 */

// why: a literal tuple, not chatAtomComponentMap.json -- christo's tsc graph reaches this file through askPuntt.ts and the JSON is Carrie's; __tests__/chatAtoms.test.ts holds the two in step.
export const ASK_PUNTT_CHAT_ATOM_IDS = [
  "ask-puntt.chat-atom.choice-row",
  "ask-puntt.chat-atom.question-form-card",
  "ask-puntt.chat-atom.question-form",
  "ask-puntt.chat-atom.review-preflight",
  "ask-puntt.chat-atom.enterprise-impact",
  "ask-puntt.chat-atom.feed-memory-card",
  "ask-puntt.chat-atom.review-list-card",
  "ask-puntt.chat-atom.metric-block",
] as const;

export const askPunttChatAtomIdSchema = z.enum(ASK_PUNTT_CHAT_ATOM_IDS);
export type AskPunttChatAtomId = z.infer<typeof askPunttChatAtomIdSchema>;

/**
 * Atoms that only render inside another atom's slot. warhol's tool refuses
 * them standalone; Carrie skips one that arrives anyway rather than drawing a
 * row with no parent.
 */
export const ASK_PUNTT_NESTED_ONLY_CHAT_ATOM_IDS: readonly AskPunttChatAtomId[] =
  ["ask-puntt.chat-atom.choice-row", "ask-puntt.chat-atom.question-form-card"];

// Payloads. Field bounds live in warhol (`MAX_TEXT` and friends); the wire has
// already been checked, so these only pin shape and vocabulary. Unknown keys
// are stripped rather than refused so a field warhol adds later does not make
// an older christo drop the whole atom.

export const chatAtomChoiceRowPayloadSchema = z.object({
  label: z.string(),
  description: z.string().optional(),
  selected: z.boolean().optional(),
  disabled: z.boolean().optional(),
});

export const chatAtomQuestionFormCardPayloadSchema = z.object({
  title: z.string().optional(),
  details: z.array(z.string()).max(3).optional(),
});

export const chatAtomQuestionOptionSchema = z.object({
  label: z.string(),
  description: z.string().optional(),
  value: z.string().optional(),
});

/** One question in a form. Structurally a subset of `askPunttUserQuestionSchema`. */
export const chatAtomQuestionSchema = z.object({
  question: z.string(),
  header: z.string(),
  options: z.array(chatAtomQuestionOptionSchema),
  multiSelect: z.boolean().optional(),
  allowFiles: z.boolean().optional(),
});
export type ChatAtomQuestion = z.infer<typeof chatAtomQuestionSchema>;

export const chatAtomQuestionFormPayloadSchema = z.object({
  questions: z.array(chatAtomQuestionSchema),
  // why: stored question-form atoms still send optionLayout; the form always stacks one option per line
  optionLayout: z.enum(["grid", "stacked"]).optional(),
});

const labelledIdSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
});

/**
 * One scope-form row. `group`: which picker group it belongs to; absent on a
 * row the client does not group. `disabled`: shown, never ticked (an expert
 * method waiting on its documents). `alwaysOn`: shown ticked, never unticked
 * (the rules Experts taught, which run in every review). `subheading`: what an
 * Expert row is, written by warhol's code; shown with the description behind
 * the row's details toggle. Empty or absent on every other row.
 */
const reviewPreflightCategorySchema = labelledIdSchema.extend({
  group: z.enum(["own", "experts", "standard"]).nullish(),
  disabled: z.boolean().optional(),
  alwaysOn: z.boolean().optional(),
  subheading: z.string().optional(),
});

export const chatAtomReviewPreflightPayloadSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  categories: z.array(reviewPreflightCategorySchema),
  selectedCategoryIds: z.array(z.string()).optional(),
  variant: z.enum(["confirm", "scope"]).optional(),
});

export const chatAtomEnterpriseImpactPayloadSchema = z.object({
  metrics: z.array(
    z.object({ id: z.string(), label: z.string(), value: z.string() }),
  ),
  /** One phrase for the whole cluster. Empty lets Carrie name it from the metrics. */
  title: z.string().optional(),
  open: z.boolean().optional(),
});

export const chatAtomFeedMemoryCardPayloadSchema = z.object({
  instruction: z.string(),
  variant: z.enum(["open", "edit", "changeScope", "editedOpen"]).optional(),
  scopes: z.array(labelledIdSchema).optional(),
  selectedScopeIds: z.array(z.string()).optional(),
  /** Where the memory was saved; warhol fills it from the save result. null is "not known", which reads "Saved to memory". */
  // why method and awaiting_expert: a Personalize keep's card names where ITS statement went -- the Expert's review method, or a rule waiting for an Expert (spec surbhi.kapoor/0092).
  savedScope: z
    .enum(["enterprise", "personal", "method", "awaiting_expert"])
    .nullable()
    .optional(),
});

export const chatAtomReviewListCardPayloadSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** null draws no badge, which is not the same as version 1. */
  versionNumber: z.number().int().min(1).nullable().optional(),
  thumbnailUrl: z.string().optional(),
  status: z.string().optional(),
  aiCommentCount: z.number().int().min(0).optional(),
  humanCommentCount: z.number().int().min(0).optional(),
});

export const chatAtomMetricTrendSchema = z.enum([
  "up",
  "down",
  "neutral",
  "up-positive",
  "up-negative",
  "down-positive",
  "down-negative",
]);

export const chatAtomMetricBlockPayloadSchema = z.object({
  label: z.string(),
  value: z.string(),
  /** Empty string is warhol's "no trend"; Carrie draws none for it. */
  trend: z.union([chatAtomMetricTrendSchema, z.literal("")]).optional(),
  size: z.enum(["default", "compact"]).optional(),
});

function atom<Id extends AskPunttChatAtomId, Payload extends z.ZodTypeAny>(
  componentId: Id,
  payload: Payload,
) {
  return z.object({
    componentId: z.literal(componentId),
    /** Unique on its message; warhol defaults it to the component id. */
    instanceId: z.string().min(1),
    payload,
  });
}

export const askPunttChatAtomSchema = z.discriminatedUnion("componentId", [
  atom("ask-puntt.chat-atom.choice-row", chatAtomChoiceRowPayloadSchema),
  atom(
    "ask-puntt.chat-atom.question-form-card",
    chatAtomQuestionFormCardPayloadSchema,
  ),
  atom("ask-puntt.chat-atom.question-form", chatAtomQuestionFormPayloadSchema),
  atom(
    "ask-puntt.chat-atom.review-preflight",
    chatAtomReviewPreflightPayloadSchema,
  ),
  atom(
    "ask-puntt.chat-atom.enterprise-impact",
    chatAtomEnterpriseImpactPayloadSchema,
  ),
  atom(
    "ask-puntt.chat-atom.feed-memory-card",
    chatAtomFeedMemoryCardPayloadSchema,
  ),
  atom(
    "ask-puntt.chat-atom.review-list-card",
    chatAtomReviewListCardPayloadSchema,
  ),
  atom("ask-puntt.chat-atom.metric-block", chatAtomMetricBlockPayloadSchema),
]);
export type AskPunttChatAtom = z.infer<typeof askPunttChatAtomSchema>;

/** The atom narrowed to one id, for a renderer that switches on `componentId`. */
// why-not: not `Extract<AskPunttChatAtom, { componentId: Id }>` -- it collapses to `never` on this zod-inferred union under christo's tsconfig (see warholChat.ts's step types); the intersection distributes and narrows.
export type AskPunttChatAtomOf<Id extends AskPunttChatAtomId> =
  AskPunttChatAtom & { componentId: Id };
